import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { useNavigate } from 'react-router-dom';

import { TemplateActions } from '../../../../../Services/_actions/index.js';
import { useSnackBar } from '../../../../FlowBuilder/Common/SnackBar/index.tsx';
import CommonTable, { EllipsisWithTooltip } from '../../../../Shared/Tables/TableV1.tsx';
import Pagination from '../../../../FlowBuilder/Common/Pagination/index.tsx';
import DynamicBreadcrumbs from '../../../../Shared/Breadcrumb/index.tsx';
import HomeIcon from '@mui/icons-material/Home';
import OptoutUserModal from '../../../../../features/whatsapp-templates/components/list/OptoutUserModal';

// ─── types ────────────────────────────────────────────────────────────────────
interface OptinRow {
  'Phone Number'?: string;
  'Company ID'?: string;
  'Opted In'?: boolean;
  'Created At'?: string;
  'Updated At'?: string;
  Source?: string;
}

// ─── helpers ──────────────────────────────────────────────────────────────────
const OptedInChip: React.FC<{ value?: boolean }> = ({ value }) => (
  <Chip label={value ? 'Opted In' : 'Opted Out'} color={value ? 'success' : 'error'} size="small" />
);

const FIVE_HOURS_THIRTY_MINS_MS = (5 * 60 + 30) * 60 * 1000;

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '—';
  try {
    let raw = dateStr;
    if (typeof raw === 'string') {
      raw = raw.trim();
    }
    const parseable =
      typeof raw === 'string' && raw.includes(' ') && !raw.includes('T')
        ? raw.replace(' ', 'T')
        : raw;
    const dateObj = new Date(parseable);
    const validDate = !Number.isNaN(dateObj.getTime()) ? dateObj : new Date(raw);
    if (Number.isNaN(validDate.getTime())) {
      return dateStr;
    }
    const adjusted = new Date(validDate.getTime() + FIVE_HOURS_THIRTY_MINS_MS);
    return adjusted.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

// ─── columns ──────────────
const buildColumns = (
  page: number,
  rowsPerPage: number,
  onOptout?: (phone: string) => void,
  optingOut?: boolean
) => [
  {
    header: '#',
    accessorKey: '_index',
    size: 55,
    enableSorting: false,
    Cell: ({ row }: any) => (
      <Typography sx={{ fontSize: 12.25, color: 'text.secondary' }}>
        {page * rowsPerPage + row.index + 1}
      </Typography>
    ),
  },
  {
    header: 'Phone Number',
    accessorKey: 'Phone Number',
    size: 170,
    Cell: ({ row }: any) => (
      <EllipsisWithTooltip
        text={row.original?.['Phone Number'] || row.original?.phone_number || '—'}
        fontWeight={500}
      />
    ),
  },
  {
    header: 'Company ID',
    accessorKey: 'Company ID',
    size: 150,
    Cell: ({ row }: any) => (
      <EllipsisWithTooltip text={row.original?.['Company ID'] || row.original?.company_id || '—'} />
    ),
  },
  {
    header: 'Source',
    accessorKey: 'Source',
    size: 130,
    Cell: ({ row }: any) => (
      <EllipsisWithTooltip text={row.original?.['Source'] || row.original?.source || '—'} />
    ),
  },
  {
    header: 'Status',
    accessorKey: 'Opted In',
    size: 120,
    Cell: ({ row }: any) => (
      <OptedInChip value={row.original?.['Opted In'] ?? row.original?.is_optin} />
    ),
  },
  {
    header: 'Created At',
    accessorKey: 'Created At',
    size: 180,
    Cell: ({ row }: any) => (
      <EllipsisWithTooltip
        text={formatDate(row.original?.['Created At'] || row.original?.created_at)}
        maxWidth={200}
      />
    ),
  },
  {
    header: 'Updated At',
    accessorKey: 'Updated At',
    size: 180,
    Cell: ({ row }: any) => (
      <EllipsisWithTooltip
        text={formatDate(row.original?.['Updated At'] || row.original?.updated_at)}
        maxWidth={200}
      />
    ),
  },
  {
    header: 'Action',
    accessorKey: 'actions',
    size: 110,
    enableSorting: false,
    Cell: ({ row }: any) => {
      const phone = row.original?.['Phone Number'] || row.original?.phone_number || '';
      const isOptedIn = row.original?.['Opted In'] ?? row.original?.is_optin ?? true;
      if (!isOptedIn) {
        return (
          <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>
            Opted out
          </Typography>
        );
      }
      return (
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={optingOut || !phone}
          onClick={() => onOptout?.(phone)}
          sx={{
            fontSize: 11.5,
            py: 0.25,
            px: 1,
            textTransform: 'none',
            minWidth: 'auto',
          }}
        >
          Opt Out
        </Button>
      );
    },
  },
];

// ─── component ────────────────────────────────────────────────────────────────
const OptinUsers: React.FC = () => {
  const actions = TemplateActions();
  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();

  // state
  const [rows, setRows] = useState<OptinRow[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [optingOut, setOptingOut] = useState<boolean>(false);
  const [optoutModalOpen, setOptoutModalOpen] = useState<boolean>(false);
  const [optoutTargetPhone, setOptoutTargetPhone] = useState<string>('');

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  // ── breadcrumbs ────────────────────────────────────────────────────────────
  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      href: '/',
    },
    { label: 'Optin Users' },
  ];

  const handleBreadcrumbClick = (event: React.MouseEvent<HTMLAnchorElement>, item: any) => {
    if (item?.href) {
      navigate(item.href);
    }
  };

  // ── fetch ─────────────────────────────────────────────────────────────────
  const fetchOptinList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await actions.getOptinList();

      if (res) {
        const formattedRows = Array.isArray(res) ? res : [res];
        setRows(formattedRows);
        setTotalCount(formattedRows.length);
      }
    } catch {
      showSnackBar('Failed to fetch optin users', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOptinList();
  }, [fetchOptinList]);

  // ── download ──────────────────────────────────────────────────────────────
  const handleDownload = async () => {
    setDownloading(true);
    try {
      await actions.downloadOptinList();
    } catch {
      showSnackBar('Failed to download optin list', 'error');
    } finally {
      setDownloading(false);
    }
  };

  // ── optout ────────────────────────────────────────────────────────────────
  const handleOpenOptout = (phone?: string) => {
    setOptoutTargetPhone(phone || '');
    setOptoutModalOpen(true);
  };

  const handleOptoutConfirm = async (phone: string) => {
    setOptingOut(true);
    try {
      const res: any = await actions.optoutUser(phone);
      const msg = res?.data?.message || res?.message || 'User opted out successfully';
      showSnackBar(msg, 'success');
      setOptoutModalOpen(false);
      setOptoutTargetPhone('');
      await fetchOptinList();
    } catch (error: any) {
      const errMsg =
        error?.response?.data?.data?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to opt out user';
      showSnackBar(errMsg, 'error');
      throw error;
    } finally {
      setOptingOut(false);
    }
  };

  // ── pagination ────────────────────────────────────────────────────────────
  const handleChangePage = (_: any, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (e: any) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const renderBottomToolbar = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e0e0e0' }}>
      <Pagination
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </Box>
  );

  const columns = buildColumns(page, rowsPerPage, handleOpenOptout, optingOut);

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px', p: 2 }}>
      {/* HEADER */}
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        {/* LEFT */}
        <Grid item>
          <DynamicBreadcrumbs items={breadcrumbsData} onBreadcrumbClick={handleBreadcrumbClick} />

          <Typography variant="h4" fontWeight="bold" sx={{ mt: 0.5 }}>
            Optin Users
          </Typography>
        </Grid>

        {/* RIGHT */}
        <Grid item>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchOptinList}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={
                downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />
              }
              onClick={handleDownload}
              disabled={downloading}
              sx={{
                fontSize: 12,
              }}
            >
              {downloading ? 'Downloading...' : 'Download'}
            </Button>

            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<PersonRemoveIcon fontSize="small" />}
              onClick={() => handleOpenOptout()}
              disabled={loading || optingOut}
              sx={{
                fontSize: 12,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Opt Out User
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 1 }} />

      {/* TABLE */}
      <CommonTable
        columns={columns}
        data={rows}
        isLoading={loading}
        enableTopToolbar={false}
        enableBottomToolbar
        enableStickyHeader
        renderBottomToolbar={renderBottomToolbar}
        state={{ isLoading: loading, sorting: [] }}
        initialState={{ density: 'compact' }}
        muiTableContainerProps={{
          sx: { maxHeight: 'calc(100vh - 260px)' },
        }}
      />

      <OptoutUserModal
        open={optoutModalOpen}
        onClose={() => {
          if (!optingOut) {
            setOptoutModalOpen(false);
            setOptoutTargetPhone('');
          }
        }}
        initialPhoneNumber={optoutTargetPhone}
        onConfirm={handleOptoutConfirm}
        isSubmitting={optingOut}
      />
    </Box>
  );
};

export default OptinUsers;
